import React from 'react';
import { 
  Star, 
  Edit, 
  Trash2, 
  User, 
  Calendar, 
  MessageSquare,
  Award,
  TrendingUp,
  Users
} from 'lucide-react';

const EvaluationList = ({
  evaluations,
  loading,
  onEdit,
  onDelete
}) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Chargement des évaluations...</span>
      </div>
    );
  }

  if (evaluations.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="bg-gray-50 rounded-xl p-8">
          <Star className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune évaluation trouvée</h3>
          <p className="text-gray-600">Commencez par créer une nouvelle évaluation</p>
        </div>
      </div>
    );
  }

  const getNoteColor = (note) => {
    if (note >= 16) return 'text-green-600 bg-green-100';
    if (note >= 14) return 'text-blue-600 bg-blue-100';
    if (note >= 12) return 'text-yellow-600 bg-yellow-100';
    if (note >= 10) return 'text-orange-600 bg-orange-100';
    return 'text-red-600 bg-red-100';
  };

  const getNoteIcon = (note) => {
    if (note >= 16) return <Award className="h-4 w-4" />;
    if (note >= 14) return <TrendingUp className="h-4 w-4" />;
    if (note >= 12) return <Star className="h-4 w-4" />;
    return <Star className="h-4 w-4" />;
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
      {evaluations.map((evaluation) => (
        <div key={evaluation.id} className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow overflow-hidden">
          {/* Header de la carte */}
          <div className="bg-gradient-to-r from-blue-50 to-blue-100 px-6 py-4 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="bg-blue-100 p-2 rounded-lg">
                  <Star className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">
                    Évaluation #{evaluation.id}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {evaluation.critere?.nom || 'Critère non spécifié'}
                  </p>
                </div>
              </div>
              <div className={`px-3 py-1 rounded-full text-sm font-medium flex items-center space-x-1 ${getNoteColor(evaluation.note)}`}>
                {getNoteIcon(evaluation.note)}
                <span>{evaluation.note}/20</span>
              </div>
            </div>
          </div>

          {/* Contenu de la carte */}
          <div className="p-6">
            {/* Informations sur l'évaluateur */}
            <div className="mb-4">
              <div className="flex items-center space-x-2 mb-2">
                <User className="h-4 w-4 text-gray-400" />
                <span className="text-sm font-medium text-gray-700">Évaluateur</span>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                {evaluation.evaluateur_info ? (
                  <div className="flex items-center space-x-2">
                    <div className={`w-2 h-2 rounded-full ${evaluation.evaluateur_info.type === 'interne' ? 'bg-blue-500' : 'bg-green-500'}`}></div>
                    <span className="text-sm text-gray-900">{evaluation.evaluateur_info.nom}</span>
                    <span className="text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded">
                      {evaluation.evaluateur_info.type === 'interne' ? 'Interne' : 'Externe'}
                    </span>
                  </div>
                ) : (
                  <span className="text-sm text-gray-500">Non spécifié</span>
                )}
              </div>
            </div>

            {/* Informations sur la soutenance */}
            {evaluation.soutenance && (
              <div className="mb-4">
                <div className="flex items-center space-x-2 mb-2">
                  <Users className="h-4 w-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-700">Soutenance</span>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-sm text-gray-900 font-medium">
                    {evaluation.soutenance.etudiant_principal_info ? 
                      `${evaluation.soutenance.etudiant_principal_info.nom} ${evaluation.soutenance.etudiant_principal_info.prenom}` :
                      evaluation.soutenance.titre_travail?.substring(0, 40) || 'Soutenance non spécifiée'
                    }
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {evaluation.soutenance.type_soutenance?.nom || 'Type non spécifié'}
                  </p>
                </div>
              </div>
            )}

            {/* Commentaire */}
            {evaluation.commentaire && (
              <div className="mb-4">
                <div className="flex items-center space-x-2 mb-2">
                  <MessageSquare className="h-4 w-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-700">Commentaire</span>
                </div>
                <div className="bg-blue-50 rounded-lg p-3">
                  <p className="text-sm text-gray-700 line-clamp-3">
                    {evaluation.commentaire}
                  </p>
                </div>
              </div>
            )}

            {/* Date d'évaluation */}
            <div className="mb-4">
              <div className="flex items-center space-x-2 mb-2">
                <Calendar className="h-4 w-4 text-gray-400" />
                <span className="text-sm font-medium text-gray-700">Date d'évaluation</span>
              </div>
              <p className="text-sm text-gray-600">
                {evaluation.date_evaluation ? 
                  new Date(evaluation.date_evaluation).toLocaleDateString('fr-FR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  }) : 
                  'Non spécifiée'
                }
              </p>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end space-x-2 pt-4 border-t border-gray-100">
              <button
                onClick={() => onEdit(evaluation)}
                className="flex items-center space-x-1 px-3 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              >
                <Edit className="h-4 w-4" />
                <span>Modifier</span>
              </button>
              <button
                onClick={() => onDelete(evaluation)}
                className="flex items-center space-x-1 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <Trash2 className="h-4 w-4" />
                <span>Supprimer</span>
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default EvaluationList; 